import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { Route } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { ProtectedRoute } from './components/ProtectedRoute';
import { getUserRole } from './utils/auth';
import { ROLES, filterMenuByRole } from './utils/roleConfig';

import { AppTopbar } from './AppTopbar';
import { AppFooter } from './AppFooter';
import { AppMenu } from './AppMenu';
import { AppConfig } from './AppConfig';

import { Dashboard } from './components/Dashboard';
import { ButtonDemo } from './components/ButtonDemo';
import { ChartDemo } from './components/ChartDemo';
import { Documentation } from './components/Documentation';
import { FileDemo } from './components/FileDemo';
import { FloatLabelDemo } from './components/FloatLabelDemo';
import { FormLayoutDemo } from './components/FormLayoutDemo';
import { InputDemo } from './components/InputDemo';
import { ListDemo } from './components/ListDemo';
import { MenuDemo } from './components/MenuDemo';
import { MessagesDemo } from './components/MessagesDemo';
import { MiscDemo } from './components/MiscDemo';
import { OverlayDemo } from './components/OverlayDemo';
import { PanelDemo } from './components/PanelDemo';
import { TableDemo } from './components/TableDemo';
import { TreeDemo } from './components/TreeDemo';
import { InvalidStateDemo } from './components/InvalidStateDemo';

import { Crud } from './pages/Crud';
import { EmptyPage } from './pages/EmptyPage';
import { TimelineDemo } from './pages/TimelineDemo';

import PrimeReact from 'primereact/api';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'prismjs/themes/prism-coy.css';
import './layout/flags/flags.css';
import './layout/layout.scss';
import './App.scss';
import { RegisterPage } from './additional-page/RegisterPage';
import { RolePage } from './additional-page/RolePage';
import { RolesPage } from './additional-page/RolesPage';
import { UsersPage } from './additional-page/UsersPage';
import { UserPage } from './additional-page/UserPage';
import { ConfirmRegisterPage } from './additional-page/ConfirmRegisterPage';
import { ForgotPasswordPage } from './additional-page/ForgotPasswordPage';
import { ChangePasswordPage } from './additional-page/ChangePasswordPage';
import { UsersConfirmationPage } from './additional-page/UsersConfirmationPage';
import { UserConfirmationPage } from './additional-page/UserConfirmationPage';
import { MailsLogPage } from './additional-page/MailsLogPage';
import { MailLogPage } from './additional-page/MailLogPage';
import { LoginPage } from './additional-page/LoginPage';
import { ValidateTokenForgotPasswordPage } from './additional-page/ValidateTokenForgotPasswordPage';
import { ConfirmRegisterMessagePage } from './additional-page/ConfirmRegisterMessagePage';
import { ConfirmForgotPasswordPage } from './additional-page/ConfirmForgotPasswordPage';
import { Unauthorized } from './pages/Unauthorized';
import { LogoutPage } from './additional-page/LogoutPage';

const App = () => {

    const [layoutMode, setLayoutMode] = useState('static');
    const [layoutColorMode, setLayoutColorMode] = useState('light')
    const [inputStyle, setInputStyle] = useState('outlined');
    const [ripple, setRipple] = useState(true);
    const [staticMenuInactive, setStaticMenuInactive] = useState(false);
    const [overlayMenuActive, setOverlayMenuActive] = useState(false);
    const [mobileMenuActive, setMobileMenuActive] = useState(false);
    const [mobileTopbarMenuActive, setMobileTopbarMenuActive] = useState(false);
    const [userRole, setUserRole] = useState(getUserRole());

    PrimeReact.ripple = true;

    let menuClick = false;
    let mobileTopbarMenuClick = false;

    useEffect(() => {
        if (mobileMenuActive) {
            addClass(document.body, "body-overflow-hidden");
        } else {
            removeClass(document.body, "body-overflow-hidden");
        }
    }, [mobileMenuActive]);

    useEffect(() => {
        const handleStorageChange = () => setUserRole(getUserRole());
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const onInputStyleChange = (inputStyle) => {
        setInputStyle(inputStyle);
    }

    const onRipple = (e) => {
        PrimeReact.ripple = e.value;
        setRipple(e.value)
    }

    const onLayoutModeChange = (mode) => {
        setLayoutMode(mode)
    }

    const onColorModeChange = (mode) => {
        setLayoutColorMode(mode)
    }

    const onWrapperClick = (event) => {
        if (!menuClick) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }

        if (!mobileTopbarMenuClick) {
            setMobileTopbarMenuActive(false);
        }

        mobileTopbarMenuClick = false;
        menuClick = false;
    }

    const onToggleMenuClick = (event) => {
        menuClick = true;

        if (isDesktop()) {
            if (layoutMode === 'overlay') {
                if(mobileMenuActive === true) {
                    setOverlayMenuActive(true);
                }

                setOverlayMenuActive((prevState) => !prevState);
                setMobileMenuActive(false);
            }
            else if (layoutMode === 'static') {
                setStaticMenuInactive((prevState) => !prevState);
            }
        }
        else {
            setMobileMenuActive((prevState) => !prevState);
        }

        event.preventDefault();
    }

    const onSidebarClick = () => {
        menuClick = true;
    }

    const onMobileTopbarMenuClick = (event) => {
        mobileTopbarMenuClick = true;

        setMobileTopbarMenuActive((prevState) => !prevState);
        event.preventDefault();
    }

    const onMobileSubTopbarMenuClick = (event) => {
        mobileTopbarMenuClick = true;

        event.preventDefault();
    }

    const onMenuItemClick = (event) => {
        if (!event.item.items) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }
    }
    const isDesktop = () => {
        return window.innerWidth >= 992;
    }

    const menu = [
        {
            label: 'Home',
            items: [{
                label: 'Dashboard', icon: 'pi pi-fw pi-home', to: '/'
            }]
        },
        {
            label: 'Menu', icon: 'pi pi-fw pi-clone',
            items: [
                {label: 'Register', icon: 'pi pi-fw pi-user-plus', to: '/register'},
                {label: 'Confirm Register', icon: 'pi pi-fw pi-user-plus', to: '/confirm-register'},
                {label: 'Roles', icon: 'pi pi-fw pi-user-plus', to: '/roles'},
                {label: 'Users', icon: 'pi pi-fw pi-user-plus', to: '/users'},
                {label: 'Forgot Password', icon: 'pi pi-fw pi-user-plus', to: '/forgot-password'},
                {label: 'Confirm Forgot Password', icon: 'pi pi-fw pi-user-plus', to: '/confirm-forgotpassword'},                
                {label: 'Users Confirmation', icon: 'pi pi-fw pi-user-plus', to: '/users-confirmation'},
                {label: 'Mails Log', icon: 'pi pi-fw pi-user-plus', to: '/mails-log'},
                {label: 'Login', icon: 'pi pi-fw pi-user-plus', to: '/login'},
                {label: 'Logout', icon: 'pi pi-fw pi-user-plus', to: '/logout'}
            ]
        },        
        {
            label: 'Menu Hierarchy', icon: 'pi pi-fw pi-search',
            items: [
                {
                    label: 'UI Kit', icon: 'pi pi-fw pi-sitemap',
                    items: [
                        {label: 'Form Layout', icon: 'pi pi-fw pi-id-card', to: '/formlayout'},
                        {label: 'Input', icon: 'pi pi-fw pi-check-square', to: '/input'},
                        {label: "Float Label", icon: "pi pi-fw pi-bookmark", to: "/floatlabel"},
                        {label: "Invalid State", icon: "pi pi-fw pi-exclamation-circle", to: "invalidstate"},
                        {label: 'Button', icon: 'pi pi-fw pi-mobile', to: '/button'},
                        {label: 'Table', icon: 'pi pi-fw pi-table', to: '/table'},
                        {label: 'List', icon: 'pi pi-fw pi-list', to: '/list'},
                        {label: 'Tree', icon: 'pi pi-fw pi-share-alt', to: '/tree'},
                        {label: 'Panel', icon: 'pi pi-fw pi-tablet', to: '/panel'},
                        {label: 'Overlay', icon: 'pi pi-fw pi-clone', to: '/overlay'},
                        {label: 'Menu', icon: 'pi pi-fw pi-bars', to: '/menu'},
                        {label: 'Message', icon: 'pi pi-fw pi-comment', to: '/messages'},
                        {label: 'File', icon: 'pi pi-fw pi-file', to: '/file'},
                        {label: 'Chart', icon: 'pi pi-fw pi-chart-bar', to: '/chart'},
                        {label: 'Misc', icon: 'pi pi-fw pi-circle-off', to: '/misc'},
                    ]
                },
                {
                    label: 'Pages', icon: 'pi pi-fw pi-clone',
                    items: [
                        {label: 'Crud', icon: 'pi pi-fw pi-user-edit', to: '/crud'},
                        {label: 'Timeline', icon: 'pi pi-fw pi-calendar', to: '/timeline'},
                        {label: 'Empty', icon: 'pi pi-fw pi-circle-off', to: '/empty'}
                    ]
                },
                {
                    label: 'Submenu 2', icon: 'pi pi-fw pi-bookmark',
                    items: [
                        {
                            label: 'Submenu 2.1', icon: 'pi pi-fw pi-bookmark',
                            items: [
                                {label: 'Submenu 2.1.1', icon: 'pi pi-fw pi-bookmark'},
                                {label: 'Submenu 2.1.2', icon: 'pi pi-fw pi-bookmark'},
                                {label: 'Submenu 2.1.3', icon: 'pi pi-fw pi-bookmark'},
                            ]
                        },
                        {
                            label: 'Submenu 2.2', icon: 'pi pi-fw pi-bookmark',
                            items: [
                                {label: 'Submenu 2.2.1', icon: 'pi pi-fw pi-bookmark'},
                                {label: 'Submenu 2.2.2', icon: 'pi pi-fw pi-bookmark'}
                            ]
                        }
                    ]
                }
            ]
        },
        {
            label: 'Get Started',
            items: [
                {label: 'Documentation', icon: 'pi pi-fw pi-question', command: () => {window.location = "#/documentation"}},
                {label: 'View Source', icon: 'pi pi-fw pi-search', command: () => {window.location = "https://github.com/primefaces/sakai-react"}}
            ]
        }
    ];

    const addClass = (element, className) => {
        if (element.classList)
            element.classList.add(className);
        else
            element.className += ' ' + className;
    }

    const removeClass = (element, className) => {
        if (element.classList)
            element.classList.remove(className);
        else
            element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }

    const wrapperClass = classNames('layout-wrapper', {
        'layout-overlay': layoutMode === 'overlay',
        'layout-static': layoutMode === 'static',
        'layout-static-sidebar-inactive': staticMenuInactive && layoutMode === 'static',
        'layout-overlay-sidebar-active': overlayMenuActive && layoutMode === 'overlay',
        'layout-mobile-sidebar-active': mobileMenuActive,
        'p-input-filled': inputStyle === 'filled',
        'p-ripple-disabled': ripple === false,
        'layout-theme-light': layoutColorMode === 'light'
    });

    return (
        <div className={wrapperClass} onClick={onWrapperClick}>
            <AppTopbar onToggleMenuClick={onToggleMenuClick} layoutColorMode={layoutColorMode}
                       mobileTopbarMenuActive={mobileTopbarMenuActive} onMobileTopbarMenuClick={onMobileTopbarMenuClick} onMobileSubTopbarMenuClick={onMobileSubTopbarMenuClick}/>

            <div className="layout-sidebar" onClick={onSidebarClick}>
                <AppMenu model={filterMenuByRole(menu, userRole)} onMenuItemClick={onMenuItemClick} layoutColorMode={layoutColorMode} />
            </div>

            <div className="layout-main-container">
                <div className="layout-main">
                    <Route path="/login" component={LoginPage}/>
                    <Route path="/unauthorized" component={Unauthorized}/>
                    <Route path="/register" component={RegisterPage}/>
                    <Route path="/confirm-register" component={ConfirmRegisterPage}/>
                    <Route path="/forgot-password" component={ForgotPasswordPage}/>
                    <Route path="/confirm-forgotpassword" component={ConfirmForgotPasswordPage}/>
                    <Route path="/validate-token-forgot-password/*" component={ValidateTokenForgotPasswordPage}/>
                    <Route path="/change-password" component={ChangePasswordPage}/>
                    <Route path="/confirm-register-message/:token" component={ConfirmRegisterMessagePage}/>

                    <ProtectedRoute path="/" exact component={Dashboard} allowedRoles={[ROLES.ADMIN, ROLES.USER, ROLES.VIEWER]}/>                                        
                    <ProtectedRoute path="/roles" component={RolesPage} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/role" component={RolePage} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/users" component={UsersPage} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/user" component={UserPage} allowedRoles={[ROLES.ADMIN]}/>                
                    <ProtectedRoute path="/users-confirmation" component={UsersConfirmationPage} allowedRoles={[ROLES.ADMIN, ROLES.USER]}/>
                    <ProtectedRoute path="/user-confirmation" component={UserConfirmationPage} allowedRoles={[ROLES.ADMIN, ROLES.USER]}/>
                    <ProtectedRoute path="/mails-log" component={MailsLogPage} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/mail-log" component={MailLogPage} allowedRoles={[ROLES.ADMIN]}/>                    
                    <ProtectedRoute path="/documentation" component={Documentation} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/logout" component={LogoutPage} allowedRoles={[ROLES.ADMIN, ROLES.USER, ROLES.VIEWER]}/>

                    <ProtectedRoute path="/formlayout" component={FormLayoutDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/input" component={InputDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/floatlabel" component={FloatLabelDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/invalidstate" component={InvalidStateDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/button" component={ButtonDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/table" component={TableDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/list" component={ListDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/tree" component={TreeDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/panel" component={PanelDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/overlay" component={OverlayDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/menu" component={MenuDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/messages" component={MessagesDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/file" component={FileDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/chart" component={ChartDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/misc" component={MiscDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/timeline" component={TimelineDemo} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/crud" component={Crud} allowedRoles={[ROLES.ADMIN]}/>
                    <ProtectedRoute path="/empty" component={EmptyPage} allowedRoles={[ROLES.ADMIN]}/>                                        
                </div>

                <AppFooter layoutColorMode={layoutColorMode}/>
            </div>

            <AppConfig rippleEffect={ripple} onRippleEffect={onRipple} inputStyle={inputStyle} onInputStyleChange={onInputStyleChange}
                       layoutMode={layoutMode} onLayoutModeChange={onLayoutModeChange} layoutColorMode={layoutColorMode} onColorModeChange={onColorModeChange} />

            <CSSTransition classNames="layout-mask" timeout={{ enter: 200, exit: 200 }} in={mobileMenuActive} unmountOnExit>
                <div className="layout-mask p-component-overlay"></div>
            </CSSTransition>

        </div>
    );

}

export default App;
